<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="share-proposal-modal-container">
            <div class="modal-header-custom">
                <h5 class="modal-title-custom">Share Proposal
                    <div class="info-button-container">
                        <button class="info-button-custom">
                            <i class="fas fa-info-circle"></i>
                        </button>
                        <div class="help-popup">
                            <div class="help-popup-content">
                                <h6>Roles Explained</h6>
                                <p><strong>Viewer:</strong> Can view the proposal, but cannot make changes or sign it.
                                </p>
                                <p><strong>Signer:</strong> Can view and electronically sign the proposal.</p>
                            </div>
                        </div>
                    </div>
                </h5>
                <button @click="closeModal" class="close-button-custom">&times;</button>
            </div>

            <div class="modal-controls">
                <input type="email" v-model.trim="newEmail" @keyup.enter="addEmail"
                    placeholder="&#xF0e0; Type an email..." class="email-input form-control" />
                <select v-model="newUserRole" class="role-selector-top">
                    <option value="viewer">Viewer</option>
                    <option value="signer">Signer</option>
                </select>
                <button @click="addEmail" class="add-button" :disabled="isUpdating">
                    {{ isUpdating ? 'Adding...' : 'Add' }}
                </button>
            </div>
            <p v-if="emailError" class="error-message">{{ emailError }}</p>

            <div class="modal-body-custom">
                <ul v-if="sharedUsers.length > 0" class="user-list">
                    <li v-for="(user, index) in sharedUsers" :key="index" class="user-item-card">
                        <div class="user-info">
                            <p class="user-email">{{ user.email }}</p>
                        </div>
                        <div class="user-actions">
                            <span class="user-role-badge">{{ user.role }}</span>
                            <button @click="revokeAccess(user.email)" class="remove-button" title="Revoke Access">&times;</button>
                        </div>
                    </li>
                </ul>
                <div v-else class="no-results">
                    <p>No users have been added to share with.</p>
                </div>
            </div>

            </div>
    </div>
</template>

<script>
import { cleanEmailString } from '@/plugins/helper.js';

export default {
    name: 'ShareProposalModal',
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        existingSharedUsers: {
            type: Array,
            default: () => []
        },
        proposalUniqueKey: {
            type: String,
            required: true,
        },
        proposalAccessKey: {
            type: String,
            required: true,
        },
        leadEmail: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            newEmail: '',
            newUserRole: 'viewer', // CHANGED: Added for the top role selector
            emailError: '',
            sharedUsers: [],
            isUpdating: false, // RENAMED: from isSharing to isUpdating
        };
    },
    watch: {
        show(newVal) {
            if (newVal) {
                // CHANGED: Filter out users whose access has been revoked
                this.sharedUsers = Array.isArray(this.existingSharedUsers)
                    ? this.existingSharedUsers
                        .filter(user => !user.is_access_revoked) // Filter first
                        .map(user => ({ // Then map the remaining users
                            email: user.email,
                            role: user.share_type,
                        }))
                    : [];
                
                this.newEmail = '';
                this.emailError = '';
                this.isUpdating = false;
                this.newUserRole = 'viewer';
            }
        }
    },
    methods: {
        closeModal() {
            // Only emit the close event, no need to send data as it's saved on each action
            this.$emit('close');
        },
        validateEmail(email) {
            const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(email).toLowerCase());
        },
        getDomain(email) {
            if (!email || email.indexOf('@') === -1) {
                return null;
            }
            return email.split('@')[1];
        },
        // CHANGED: addEmail now triggers an immediate API call
        async addEmail() {
            this.emailError = '';
            if (this.newEmail) {
                this.newEmail = cleanEmailString(this.newEmail);
            }
            if (!this.newEmail) return;

            if (this.newEmail.toLowerCase() === this.leadEmail.toLowerCase()) {
                this.emailError = 'You cannot share the proposal with the client lead\'s email.';
                return;
            }
            if (!this.validateEmail(this.newEmail)) {
                this.emailError = 'Please enter a valid email address.';
                return;
            }
            if (this.sharedUsers.some(user => user.email.toLowerCase() === this.newEmail.toLowerCase())) {
                this.emailError = 'This email has already been added.';
                return;
            }

            const leadDomain = this.getDomain(this.leadEmail);
            const newEmailDomain = this.getDomain(this.newEmail);

            if (leadDomain && newEmailDomain !== leadDomain) {
                this.emailError = `Email must have the same domain as the client lead (@${leadDomain}).`;
                return;
            }
            
            const newUser = { email: this.newEmail, role: this.newUserRole };
            const updatedUserList = [...this.sharedUsers, newUser];

            const success = await this.updateSharedUsersOnServer(updatedUserList, false);

            if (success) {
                this.newEmail = ''; // Clear input on successful addition
                this.newUserRole = 'viewer';
            }
        },
        // NEW: revokeAccess method to handle removing a user
        async revokeAccess(emailToRemove) {
            const updatedUserList = this.sharedUsers.filter(user => user.email !== emailToRemove);
            await this.updateSharedUsersOnServer(updatedUserList, true);
        },
        // REFACTORED: Centralized API call logic
        async updateSharedUsersOnServer(userList, revoke = false) {
            this.isUpdating = true;
            this.emailError = '';

            const sEmails = userList.map(user => ({
                email: user.email,
                share_type: user.role,
            }));

            const payload = {
                unique_key: this.proposalUniqueKey,
                access_key: this.proposalAccessKey,
                shared_emails: sEmails,
            };

            try {
                const response = await this.$axios_internal.post('/proposals/share', payload);
                if (response.data.success) {
                    // Update the local state ONLY after a successful API call
                    this.sharedUsers = userList; 
                    if(revoke) {
                        this.$emit('revoke-successful', sEmails);
                    } else {
                        this.$emit('share-successful', sEmails);
                    }
                    
                    return true;
                } else {
                    this.emailError = response.data.message || 'An unknown error occurred.';
                    this.$emit('share-failed', this.emailError);
                    return false;
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update share settings.';
                this.emailError = errorMessage;
                this.$emit('share-failed', errorMessage);
                return false;
            } finally {
                this.isUpdating = false;
            }
        },
    },
};
</script>

<style scoped>
/* --- Main Modal Styles (Referenced from Sample Contracts) --- */
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

/* ... (previous styles for modal-overlay, share-proposal-modal-container, etc. remain the same) ... */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
}

.share-proposal-modal-container {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 550px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;
    position: relative;
}

.modal-title-custom {
    font-size: 1.25rem;
    font-weight: 500;
    margin: 0;
    display: flex; /* Ensure title and info button are inline */
    align-items: center;
    gap: 8px;
}

.close-button-custom {
    background: none;
    border: none;
    font-size: 1.75rem;
    font-weight: bold;
    line-height: 1;
    color: #6c757d;
    cursor: pointer;
    opacity: 0.7;
}

.close-button-custom:hover {
    opacity: 1;
}

/* --- Controls Section for Email Input --- */
.modal-controls {
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    gap: 10px;
    border-bottom: 1px solid #e9ecef;
}

.form-control {
    flex-grow: 1;
    border-radius: 8px;
    height: 40px;
    font-size: 14px;
    font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free';
    font-weight: 900;
    padding: 10px 12px;
    border: 1px solid #ced4da;
}

.form-control:focus {
    outline: none;
    border-color: #00a0c1;
    box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.25);
}

/* NEW: Style for the role selector at the top */
.role-selector-top {
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 0 10px;
    height: 40px;
    font-size: 0.9rem;
    background-color: white;
    flex-shrink: 0; /* Prevents it from shrinking */
}

.add-button {
    background-color: #00a0c1;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0 20px;
    height: 40px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    flex-shrink: 0;
}

.add-button:hover {
    background-color: #008da9;
}

.add-button:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
}

.error-message {
    color: #dc3545;
    font-size: 0.875rem;
    padding: 0 1.5rem;
    margin-top: 0.5rem;
    min-height: 1.2em; /* Prevents layout shift */
}

/* --- Body Section for User List --- */
.modal-body-custom {
    padding: 1rem;
    overflow-y: auto;
    flex-grow: 1;
    background-color: #f8f9fa;
    min-height: 150px; /* Give the body a minimum height */
}

.user-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.user-item-card {
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.user-item-card:last-child {
    margin-bottom: 0;
}

.user-info {
    flex-grow: 1;
}

.user-email {
    font-weight: 600;
    color: #343a40;
    margin: 0;
}

.user-actions {
    display: flex;
    align-items: center;
    gap: 15px;
}

/* NEW: Style for the read-only role badge */
.user-role-badge {
    background-color: #e9ecef;
    color: #495057;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
    text-transform: capitalize;
}


.remove-button {
    background: none;
    border: none;
    color: #6c757d;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    line-height: 1;
    padding: 0 5px;
}

.remove-button:hover {
    color: #dc3545;
}

.no-results {
    padding: 2rem;
    text-align: center;
    color: #6c757d;
}

/* --- REMOVED: modal-footer-custom and share-button styles --- */

/* --- Updated Styles for Hover-triggered Info Pop-up (No changes here) --- */
.info-button-container {
    position: relative;
    display: inline-block;
    height: 1.25rem;
}

.info-button-custom {
    background: none;
    border: none;
    font-size: 1rem;
    color: #00a0c1;
    cursor: default;
    padding: 0;
    line-height: 1;
    transition: color 0.2s;
}

/* ... (rest of the pop-up styles remain the same) ... */
.info-button-custom:hover {
    color: #008da9;
}

.help-popup {
    visibility: hidden; /* Hidden by default */
    opacity: 0; /* Start fully transparent */
    position: absolute;
    top: 100%; /* Position below the info button */
    left: 50%;
    transform: translateX(-50%);
    z-index: 1060;
    width: 280px; /* Slightly smaller for a more compact look */
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 1rem;
    transition: opacity 0.3s ease, visibility 0.3s ease; /* Smooth transition */
    text-align: left; /* Align text within the popup */
}

.info-button-container:hover .help-popup {
    visibility: visible; /* Show on hover */
    opacity: 1; /* Fade in */
}

.help-popup::before {
    content: '';
    position: absolute;
    bottom: 100%; /* Position above the popup */
    left: 50%;
    transform: translateX(-50%);
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #fff; /* White arrow to match background */
    filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.05)); /* Subtle shadow for the arrow */
}

.help-popup-content h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-weight: 700; /* Bolder for titles */
    color: #343a40;
    font-size: 1rem; /* Slightly larger title */
}

.help-popup-content p {
    font-size: 0.875rem; /* Slightly smaller body text */
    margin-bottom: 0.5rem;
    line-height: 1.5; /* Improved readability */
    color: #555; /* Slightly darker grey for better contrast */
}

.help-popup-content p strong {
    color: #333; /* Make strong text stand out more */
}
</style>